import type { ExportDto } from '@octopus-do/octopus-api-client'

import { Badges, SectionLabels } from './constants'

type ExportItems = ExportDto['items']

const CSV_HEADER = ['URL', 'Section', 'Status', 'Tags', 'Redirects to']

// Excel reads a CSV in the local codepage unless the file says otherwise, and
// a report is nothing but URLs, which are exactly where the accents live.
const BOM = '\ufeff'

const csvCell = (value: string) =>
  /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value

const csvRow = (cells: string[]) => cells.map(csvCell).join(',')

// The badge labels, not the wire kinds: this file is read by a person, and
// "Missing H1" says what `missing_h1` means.
const toTags = (badges: ExportItems[number]['badges']) =>
  badges
    .map((kind) => Badges[kind]?.title)
    .filter(Boolean)
    .join(', ')

export const toCsv = (items: ExportItems): string =>
  BOM +
  [
    csvRow(CSV_HEADER),
    ...items.map((item) =>
      csvRow([
        item.url,
        SectionLabels[item.section],
        item.status === null ? '' : String(item.status),
        toTags(item.badges),
        item.redirectTarget ?? '',
      ])
    ),
  ].join('\n')

const XML_ESCAPES: Record<string, string> = {
  '"': '&quot;',
  '&': '&amp;',
  "'": '&apos;',
  '<': '&lt;',
  '>': '&gt;',
}

// A query string with an `&` in it is a valid URL and an invalid XML document.
const xmlText = (value: string) =>
  value.replace(/["&'<>]/g, (character) => XML_ESCAPES[character])

// `lastmod` is the day the crawl ran. The file describes that crawl, so dating
// it "today" would tell a search engine the site changed when nothing did.
const toLastmod = (crawledAt: null | string): null | string => {
  if (!crawledAt) {
    return null
  }

  const date = new Date(crawledAt)

  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10)
}

export const toSitemapXml = (
  items: ExportItems,
  crawledAt: null | string
): string => {
  const lastmod = toLastmod(crawledAt)
  const doc = [
    '<?xml version="1.0" encoding="utf-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    '<!--  created with sitemap generator https://octopus.do  -->',
    '',
  ]

  items.forEach((item) => {
    doc.push('    <url>')
    doc.push(`        <loc>${xmlText(item.url)}</loc>`)

    if (lastmod) {
      doc.push(`        <lastmod>${lastmod}</lastmod>`)
    }

    doc.push('    </url>')
  })

  doc.push('</urlset>')

  return doc.join('\n')
}

// sitemap.xml has to be called sitemap.xml wherever it is going, so only the
// CSV takes the site's name — otherwise two reports downloaded in a row are
// two files nobody can tell apart.
export const csvFileName = (siteUrl: string): string => {
  try {
    return `${new URL(siteUrl).host.replace(/^www\./, '')}-report.csv`
  } catch {
    return 'report.csv'
  }
}

export const downloadFile = (name: string, content: string, type: string) => {
  const blob = new Blob([content], { type })
  const link = document.createElement('a')

  link.download = name
  link.href = window.URL.createObjectURL(blob)
  link.click()
  window.URL.revokeObjectURL(link.href)
}
